import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, OverlayTrigger, Tooltip, Popover } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import UserSidebar from "./UserSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import usePagedList, { sortToOrderBy } from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";
import { deactivateData, restoreData } from "../../utils/softDelete";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Users",
    SEARCH_PLACEHOLDER: "Search",
    DELETE_TOOLTIP_HINT: "Please select local users only",
    // Delete confirmation
    DELETE_TITLE: "Delete User(s)?",
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    // Success messages
    DELETE_SUCCESS: "User(s) deleted successfully",
    // Error messages
    DELETE_ERROR: "Failed to delete user(s)",
    NOT_FOUND: "User not found",
};

// Confirmation messages
const USER_MESSAGES = {
    DELETE_USERS_SINGLE: (name) => `You are about to delete user "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,

    DELETE_USERS_BULK: (count) => `You are about to delete ${count} user(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

// Everything the table and the sidebar read off a user.
const USER_FIELDS = `
            id
            email
            name
            username
            is_active
            deleted_at
            description
            home_timezone
            current_timezone
            show_time_in_home_timezone
            identities {
                provider
            }
            memberships {
                plant {
                    id
                    name
                }
                role {
                    id
                    name
                }
            }
`;

// One page of users plus the total, for server-side pagination (usePagedList).
const GET_USERS = gql`
    query Users(
        $where: UserWhereInput
        $orderBy: [UserOrderByWithRelationInput!]
        $take: Int
        $skip: Int
    ) {
        users(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
            ${USER_FIELDS}
        }
        aggregateUser(where: $where) {
            _count {
                _all
            }
        }
    }
`;

// A deep link (/users/:id) may point at a user that is not on the current page.
// findFirst goes through the soft-delete filter, so an archived user is not found.
const GET_USER_BY_ID = gql`
    query GetUserById($where: UserWhereInput) {
        findFirstUser(where: $where) {
            ${USER_FIELDS}
        }
    }
`;

// The signed-in user's identity providers: a local-only user does not see users
// that sign in through the Starek identity provider.
const GET_CURRENT_USER_IDENTITIES = gql`
    query CurrentUserIdentities($where: UserWhereUniqueInput!) {
        user(where: $where) {
            id
            identities {
                provider
            }
        }
    }
`;

const UPDATE_MANY_USER = gql`
    mutation UpdateManyUser($data: UserUpdateManyMutationInput!, $where: UserWhereInput) {
        updateManyUser(data: $data, where: $where) {
            count
        }
    }
`;

const DELETE_ONE_USER = gql`
    mutation DeleteOneUser($id: Int!) {
        deleteOneUser(where: { id: $id }) {
            id
        }
    }
`;

const DELETE_MANY_USER = gql`
    mutation DeleteManyUser($ids: [Int!]!) {
        deleteManyUser(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const formatIdentityProviders = (row) =>
    (row?.identities || []).map((i) => i?.provider).filter(Boolean);

const formatMemberships = (row) =>
    (row?.memberships || [])
        .map((m) => {
            const plant = m?.plant?.name || "-";
            const role = m?.role?.name || "-";
            return `${plant}: ${role}`;
        })
        .filter(Boolean);

/**
 * Search box -> where fragment. Same reach as the old client-side filter: name, email,
 * description, and the plant / role names of the user's memberships.
 */
const userSearch = (text) => {
    const q = text?.trim();
    if (!q) return null;
    return {
        OR: [
            { name: { contains: q } },
            { email: { contains: q } },
            { description: { contains: q } },
            {
                memberships: {
                    some: {
                        OR: [
                            { plant: { is: { name: { contains: q } } } },
                            { role: { is: { name: { contains: q } } } },
                        ],
                    },
                },
            },
        ],
    };
};

const isOnlyLocalIdentity = (row) => {
    const providers = formatIdentityProviders(row);
    return (
        providers.length > 0 &&
        providers.every((p) => p === "local-identity-provider")
    );
};

/**
 * A local-only user does not see Starek-identity users. That used to be a client-side
 * filter over the whole list; with paging it has to be part of the server query, so
 * the signed-in user's own identities are read before the list mounts. Mounting the
 * list only once that is known keeps its first where final, so a deep link to
 * ?page=N is not reset to page 1 when the identity answer arrives.
 */
const Users = () => {
    const starekContext = useContext(StarekContext);
    const currentUserId = Number.parseInt(String(starekContext?.user?.id ?? ""), 10);
    const { data, loading } = useQuery(GET_CURRENT_USER_IDENTITIES, {
        client: starekContext.engineClient,
        skip: !currentUserId,
        variables: { where: { id: currentUserId } },
        fetchPolicy: "network-only",
    });

    if (loading) return <EntityLoadingState icon="fa-user" />;

    return (
        <UsersList
            currentUserIsLocal={!!data?.user && isOnlyLocalIdentity(data.user)}
        />
    );
};

const UsersList = ({ currentUserIsLocal }) => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeUserId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite   = can("user", "WRITE");
    const canDestroy = can("user", "DESTROY");

    const [searchParams, setSearchParams] = useSearchParams();

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const setShowInactive = (value, options = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value) {
            next.set("showInactive", "1");
        } else {
            next.delete("showInactive");
        }
        setSearchParams(next, options);
    };

    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [routeEditUser, setRouteEditUser] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Ids just deleted: the refresh drops them while the route may still point at one.
    const deletedIdsRef = useRef(new Set());

    // Starek-identity users stay out of a local-only user's list and deep links alike.
    const identityWhere = useMemo(
        () =>
            currentUserIsLocal
                ? {
                      identities: {
                          none: { provider: { equals: "starek-identity-provider" } },
                      },
                  }
                : {},
        [currentUserIsLocal],
    );

    const userQueryWhere = useMemo(
        () => ({
            ...(showInactive ? {} : { is_active: { equals: true } }),
            ...identityWhere,
        }),
        [showInactive, identityWhere],
    );

    // Table sort, applied on the server: { field, direction } or null for default order.
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => sortToOrderBy(sort, ["name", "email"]), [sort]);

    // One page (12) of users from the server, network-only on every run.
    const {
        items,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading,
        initialLoading,
        error,
        refetch,
    } = usePagedList({
        query: GET_USERS,
        listField: "users",
        countField: "aggregateUser",
        where: userQueryWhere,
        searchText: filterText,
        buildSearch: userSearch,
        orderBy,
        client: starekContext.engineClient,
    });

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);

    // /users/:id may point at a user that is not on this page, so it is loaded on its
    // own (whatever its is_active; archived users are excluded server-side).
    const {
        data: routeUserData,
        loading: routeUserLoading,
        refetch: refetchRouteUser,
    } = useQuery(GET_USER_BY_ID, {
        client: starekContext.engineClient,
        skip: routeUserId == null,
        variables: { where: { id: { equals: routeUserId }, ...identityWhere } },
        fetchPolicy: "network-only",
    });

    /**
     * Re-reads the page after a write, and the open user too when it is not on the
     * page, so the sidebar sees the write. A just-deleted user is skipped — it would
     * read as "not found". A failed refetch must not take the write down with it.
     */
    const hardRefresh = async () => {
        try {
            const res = await refetch();
            const onPage = (res?.data?.users ?? []).some(
                (u) => Number(u.id) === routeUserId,
            );
            if (
                routeUserId != null &&
                !deletedIdsRef.current.has(routeUserId) &&
                !onPage
            ) {
                await refetchRouteUser();
            }
        } catch (err) {
            console.error("Failed to refresh users:", err);
        }
    };

    const [deleteOneUser, { loading: deletingOne }] = useMutation(
        DELETE_ONE_USER,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteManyUser, { loading: deletingMany }] = useMutation(
        DELETE_MANY_USER,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateManyUser] = useMutation(UPDATE_MANY_USER, { client: starekContext.engineClient });

    const isDeleting = deletingOne || deletingMany;

    const localOnlySelection =
        selectedRows.length > 0 && selectedRows.every(isOnlyLocalIdentity);
    const canDelete = selectedRows.length > 0 && localOnlySelection;
    const anyNonLocalSelected =
        selectedRows.length > 0 &&
        selectedRows.some((r) => !isOnlyLocalIdentity(r));
    const showDeleteTooltip = anyNonLocalSelected;

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error, starekContext]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditUser(null);
            return;
        }

        if (routeUserLoading) return;

        const listRoute = buildListRoute("/users", searchParams);

        if (routeUserId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditUser(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Just deleted from the sidebar: the sidebar closes itself. Checked before the
        // lookup, because the by-id result can still hold the deleted row for a moment.
        if (deletedIdsRef.current.has(routeUserId)) return;

        // Prefer the row on the current page (fresh after every refresh); otherwise the
        // user loaded by id. Either way inactive users open without widening the list.
        const userToEdit =
            items.find((user) => Number(user?.id) === routeUserId) ||
            routeUserData?.findFirstUser;

        if (!userToEdit) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditUser(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditUser(userToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeUserId
                ? current
                : [userToEdit],
        );
        setShowWizard(true);
    }, [
        routeId,
        routeUserId,
        routeUserLoading,
        routeUserData,
        items,
        searchParams,
        navigate,
        starekContext,
    ]);

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: { style: { minHeight: "60px" } },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    const handleAdd = () => {
        setRouteEditUser(null);
        setSelectedRows([]);
        setShowWizard(true);
    };

    // Keep the list's URL state (?page, ?showInactive) across opening and closing a user.
    const openUser = (id) => navigate(buildListRoute(`/users/${id}`, searchParams));

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        openUser(selectedRows[0].id);
    };

    const handleDrawerClose = async () => {
        setShowWizard(false);
        setRouteEditUser(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            navigate(buildListRoute("/users", searchParams), { replace: true });
        }
        await hardRefresh();
    };

    const handleDelete = () => {
        if (selectedRows.length === 0) return;

        const count = selectedRows.length;
        const userName =
            selectedRows[0]?.name || selectedRows[0]?.email || "Unknown";

        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content:
                count === 1
                    ? USER_MESSAGES.DELETE_USERS_SINGLE(userName)
                    : USER_MESSAGES.DELETE_USERS_BULK(count),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                try {
                    const ids = selectedRows.map((u) => Number(u.id));
                    if (ids.length === 1) {
                        await deleteOneUser({ variables: { id: ids[0] } });
                    } else {
                        await deleteManyUser({ variables: { ids } });
                    }
                    ids.forEach((id) => deletedIdsRef.current.add(id));
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await hardRefresh();
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const NameCell = ({ row }) => (
        <span
            role="button"
            className="text-primary"
            onClick={() => openUser(row.id)}
        >
            {row.name || "-"}
        </span>
    );

    const activeUser = routeEditUser || selectedRows[0] || null;

    const MembershipsCell = ({ row }) => {
        const memberships = formatMemberships(row);
        const firstTwo = memberships.slice(0, 2);
        const extra = memberships.length - firstTwo.length;

        const pop = (
            <Popover id={`membership-pop-${row.id}`}>
                <Popover.Body className="p-2">
                    {memberships.length === 0 ? (
                        <div className="text-muted">No memberships</div>
                    ) : (
                        memberships.map((m, i) => (
                            <div key={i} className="py-1">
                                {m}
                            </div>
                        ))
                    )}
                </Popover.Body>
            </Popover>
        );

        return (
            <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
            >
                {firstTwo.map((m) => (
                    <Badge key={m} bg="light" text="dark" className="border">
                        {m}
                    </Badge>
                ))}
                {extra > 0 && (
                    <OverlayTrigger
                        trigger="click"
                        placement="right"
                        overlay={pop}
                        rootClose
                    >
                        <Badge
                            bg="light"
                            text="dark"
                            className="border"
                            role="button"
                        >
                            +{extra} more
                        </Badge>
                    </OverlayTrigger>
                )}
            </div>
        );
    };

    const IdentityProvidersCell = ({ row }) => {
        const providers = formatIdentityProviders(row);
        return (
            <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
            >
                {providers.map((p) => (
                    <Badge key={p} bg="light" text="dark" className="border">
                        {p}
                    </Badge>
                ))}
            </div>
        );
    };

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            sortField: "name",
            cell: (row) => <NameCell row={row} />,
            grow: 1.1,
        },
        {
            name: "Email",
            selector: (r) => r.email || "-",
            sortable: true,
            sortField: "email",
            grow: 1.2,
        },
        {
            name: "Memberships",
            selector: (r) => formatMemberships(r).join(", "),
            sortable: false,
            cell: (row) => <MembershipsCell row={row} />,
            grow: 1.6,
        },
        {
            name: "Identity Provider",
            selector: (r) => formatIdentityProviders(r).join(", "),
            sortable: false,
            cell: (row) => <IdentityProvidersCell row={row} />,
            grow: 1.2,
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: false,
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: 420 }}>
                    {row.description || "-"}
                </div>
            ),
            grow: 2,
        },
    ];

    const handleToggleActive = async (users, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = users.map((u) => u.id);
        try {
            await updateManyUser({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} user(s)` : `Deactivated ${ids.length} user(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const userExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    const userConditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    // Only the first load gets the full-page spinner; later loads (Show Inactive, a
    // page change, a refetch after a write) keep the list and any open sidebar on screen.
    if (initialLoading) return <EntityLoadingState icon="fa-user" />;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "User", plural: "Users" }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={userExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); setToggleCleared((p) => !p); }}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDisabled={!canDelete}
                deleteTooltip={
                    showDeleteTooltip ? UI_STRINGS.DELETE_TOOLTIP_HINT : null
                }
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={handleDelete}
            >
                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip="List View"
                >
                    {/* `items` is the current page, searched and sorted on the server; the
                        Paginator below handles paging, so the table's own pager is off. */}
                    <DataTable
                        columns={columns}
                        data={items}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        striped
                        clearSelectedRows={toggleCleared}
                        highlightOnHover
                        pointerOnHover
                        onRowClicked={(row) => openUser(row.id)}
                        responsive
                        dense
                        sortServer
                        onSort={(column, direction) =>
                            setSort(column.sortField ? { field: column.sortField, direction } : null)
                        }
                        selectableRowsHighlight
                        selectableRowsNoSelectAll={false}
                        customStyles={customStyles}
                        noDataComponent={<EntityEmptyState icon="fa-user" entityName="User" messages={EMPTY_MESSAGES.users} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        conditionalRowStyles={userConditionalRowStyles}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <Paginator
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                total={total}
                onChange={setPage}
                disabled={loading}
            />

            <UserSidebar
                show={showWizard}
                mode={activeUser?.id ? "edit" : "add"}
                initialValues={activeUser || {}}
                onClose={handleDrawerClose}
                onFinish={handleDrawerClose}
                onToggleActive={canWrite ? (entity) => handleToggleActive([entity], !entity.is_active) : undefined}
                readOnly={!canWrite}
            />

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type || "danger"}
                title={confirmCfg.title}
                content={confirmCfg.content}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Users };
